import { useState, useEffect, useCallback } from 'react';
import {
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { ColumnConfigItem, CustomColumnDefinition, CustomColumnType } from '../../../types/lead';
import { DEFAULT_LEAD_COLUMNS } from '../../../hooks/leads/useLeadColumns';
import { useNotification } from '../../../contexts/NotificationContext';
import { validateRenameItem, normalizeLabel } from '../../../utils/leads/leadsUtils';
import { extractErrorMessage } from '../../../utils/errorUtils';

interface UseManageLeadColumnsProps {
  open: boolean;
  onClose: () => void;
  resolvedColumns: ColumnConfigItem[];
  customColumns: CustomColumnDefinition[];
  onSaveTableConfig: (updated: ColumnConfigItem[]) => Promise<any>;
  onCreateCustomColumn: (name: string, type: CustomColumnType, options?: string[]) => Promise<any>;
  onPatchCustomColumn: (key: string, patchPayload: { name?: string; options?: string[] }) => Promise<any>;
  onDeactivateCustomColumn: (key: string) => Promise<any>;
  initialTab?: number;
}

export function useManageLeadColumns({
  open,
  onClose,
  resolvedColumns,
  customColumns,
  onSaveTableConfig,
  onCreateCustomColumn,
  onPatchCustomColumn,
  onDeactivateCustomColumn,
  initialTab = 0,
}: UseManageLeadColumnsProps) {
  const { showNotification } = useNotification();
  const [activeTab, setActiveTab] = useState<number>(initialTab);
  const [columnsState, setColumnsState] = useState<ColumnConfigItem[]>(resolvedColumns);
  const [isUserModified, setIsUserModified] = useState<boolean>(false);
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

  // New Custom Column Form State (Creation Mode)
  const [newColName, setNewColName] = useState<string>('');
  const [newColType, setNewColType] = useState<CustomColumnType>('text');
  const [optionsList, setOptionsList] = useState<string[]>([]);
  const [currentOptionInput, setCurrentOptionInput] = useState<string>('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [createOptionErrors, setCreateOptionErrors] = useState<Record<number, string>>({});

  // Inline Rename / Option Edit State (Editing Mode)
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [initialEditingName, setInitialEditingName] = useState<string>('');
  const [renameError, setRenameError] = useState<string | null>(null);
  
  // Unified flat array of option strings for editing
  const [editingOptions, setEditingOptions] = useState<string[]>([]);
  const [initialEditingOptions, setInitialEditingOptions] = useState<string[]>([]);
  const [editingOptionInput, setEditingOptionInput] = useState<string>('');
  const [editingAddError, setEditingAddError] = useState<string | null>(null);
  const [editingOptionErrors, setEditingOptionErrors] = useState<Record<number, string>>({});
  const [isChoiceDialogOpen, setIsChoiceDialogOpen] = useState<boolean>(false);

  // Deactivate Confirmation Dialog State
  const [deletingCol, setDeletingCol] = useState<{ key: string; label: string } | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (open) {
      setColumnsState(resolvedColumns);
    }
  }, [open, resolvedColumns]);

  useEffect(() => {
    if (open) {
      setIsUserModified(false);
      setNewColName('');
      setNewColType('text');
      setOptionsList([]);
      setCurrentOptionInput('');
      setNameError(null);
      setOptionsError(null);
      setCreateOptionErrors({});

      setEditingKey(null);
      setEditingName('');
      setInitialEditingName('');
      setRenameError(null);
      setEditingOptions([]);
      setInitialEditingOptions([]);
      setEditingOptionInput('');
      setEditingAddError(null);
      setEditingOptionErrors({});
      setIsChoiceDialogOpen(false);
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  useEffect(() => {
    if (highlightedKey && activeTab === 0) {
      const scrollTimer = setTimeout(() => {
        const el = document.getElementById(`column-item-${highlightedKey}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);

      const clearTimer = setTimeout(() => {
        setHighlightedKey(null);
      }, 3000);

      return () => {
        clearTimeout(scrollTimer);
        clearTimeout(clearTimer);
      };
    }
  }, [highlightedKey, activeTab]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnsState((items) => {
      const oldIndex = items.findIndex((item) => item.key === active.id);
      const newIndex = items.findIndex((item) => item.key === over.id);
      const reordered = arrayMove(items, oldIndex, newIndex);
      return reordered.map((col, idx) => ({ ...col, order: idx }));
    });
    setIsUserModified(true);
  }, []);

  const handleToggleVisibility = useCallback((key: string) => {
    setColumnsState((prev) =>
      prev.map((col) => {
        if (col.key === key) {
          return { ...col, visible: !(col.visible ?? true) };
        }
        return col;
      })
    );
    setIsUserModified(true);
  }, []);

  const handleSaveConfig = async () => {
    try {
      const updated = columnsState.map((col, idx) => ({
        key: col.key,
        label: col.label,
        is_default: col.is_default,
        order: idx,
        visible: col.visible ?? true,
      }));
      await onSaveTableConfig(updated);
      setIsUserModified(false);
      showNotification('Table column configuration saved successfully!', 'success');
      onClose();
    } catch (err: any) {
      const errMsg = extractErrorMessage(err, 'Failed to save column configuration');
      showNotification(errMsg, 'error');
    }
  };

  const checkNameCollision = (name: string, ignoreKey?: string) => {
    const existingItems = [
      ...DEFAULT_LEAD_COLUMNS.map((col) => ({ id: col.key, label: col.label || col.key })),
      ...customColumns
        .filter((c) => c.is_active !== false)
        .map((col) => ({ id: col.key, label: col.name })),
    ];
    return validateRenameItem(name, existingItems, ignoreKey, 'Column');
  };

  // --- Creation Mode Helpers ---
  const handleAddOption = () => {
    setOptionsError(null);
    const raw = currentOptionInput.trim();
    if (!raw) {
      setOptionsError('Please enter an option label before adding.');
      return;
    }
    if (raw.length > 25) {
      setOptionsError('Option label cannot exceed 25 characters.');
      return;
    }

    const normVal = normalizeLabel(raw);
    const exists = optionsList.some((opt) => normalizeLabel(opt) === normVal);

    if (exists) {
      setOptionsError('This option already exists.');
      return;
    }

    setOptionsList((prev) => [...prev, raw]);
    setCurrentOptionInput('');
  };

  const handleRemoveOption = useCallback((indexToRemove: number) => {
    setOptionsError(null);
    setOptionsList((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    setCreateOptionErrors((prev) => {
      const copy = { ...prev };
      delete copy[indexToRemove];
      return copy;
    });
  }, []);

  const handleCreateOptionRename = useCallback((index: number, newLabel: string) => {
    setOptionsList((prev) => {
      const updated = [...prev];
      updated[index] = newLabel;
      return updated;
    });

    const trimmed = newLabel.trim();
    const norm = normalizeLabel(newLabel);

    setCreateOptionErrors((prev) => {
      const copy = { ...prev };
      if (!trimmed) {
        copy[index] = 'Option label cannot be empty.';
      } else if (newLabel.length > 25) {
        copy[index] = 'Option label cannot exceed 25 characters.';
      } else {
        const hasDup = optionsList.some((other, idx) => idx !== index && normalizeLabel(other) === norm);
        if (hasDup) {
          copy[index] = 'This option already exists.';
        } else {
          delete copy[index];
        }
      }
      return copy;
    });
  }, [optionsList]);

  const handleTypeChange = (type: CustomColumnType) => {
    setNewColType(type);
    setOptionsList([]);
    setCurrentOptionInput('');
    setOptionsError(null);
    setCreateOptionErrors({});
  };

  const handleCreateColumn = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(null);
    setOptionsError(null);

    const trimmedName = newColName.trim();
    if (!trimmedName) {
      setNameError('Please enter a column name.');
      return;
    }

    const collisionError = checkNameCollision(trimmedName);
    if (collisionError) {
      setNameError(collisionError);
      return;
    }

    let parsedOptions: string[] | undefined;
    if (newColType === 'select' || newColType === 'multi_select') {
      if (!optionsList.length) {
        setOptionsError('Please add at least one choice option using the + Add button.');
        return;
      }
      if (Object.keys(createOptionErrors).length > 0) {
        setOptionsError('Please fix choice option errors before saving.');
        return;
      }
      parsedOptions = optionsList.map((o) => o.trim());
    }

    try {
      const res = await onCreateCustomColumn(trimmedName, newColType, parsedOptions);
      const newKey = res?.newColumn?.key || res?.key;
      showNotification(`Column "${trimmedName}" created successfully!`, 'success');
      if (newKey) {
        setHighlightedKey(newKey);
      }
      setNewColName('');
      setOptionsList([]);
      setCurrentOptionInput('');
      setNewColType('text');
      setNameError(null);
      setOptionsError(null);
      setCreateOptionErrors({});
      setActiveTab(0);
    } catch (err: any) {
      const errMsg = extractErrorMessage(err, 'Failed to create column');
      setNameError(errMsg);
      if (newColType === 'select' || newColType === 'multi_select') {
        setOptionsError(errMsg);
      }
      showNotification(errMsg, 'error');
    }
  };

  // --- Editing Mode Helpers ---
  const handleStartRename = useCallback((key: string) => {
    const col = customColumns.find((c) => c.key === key);
    if (col) {
      setEditingKey(key);
      setEditingName(col.name);
      setInitialEditingName(col.name);

      const rawOpts = col.options || [];
      setEditingOptions([...rawOpts]);
      setInitialEditingOptions([...rawOpts]);
      setEditingOptionInput('');
      setEditingAddError(null);
      setEditingOptionErrors({});
      setRenameError(null);

      if (col.type === 'select' || col.type === 'multi_select') {
        setIsChoiceDialogOpen(true);
      } else {
        setIsChoiceDialogOpen(false);
      }
    }
  }, [customColumns]);

  const handleCloseChoiceDialog = useCallback(() => {
    setIsChoiceDialogOpen(false);
    setEditingKey(null);
    setEditingName('');
    setInitialEditingName('');
    setEditingOptions([]);
    setInitialEditingOptions([]);
    setEditingOptionInput('');
    setEditingAddError(null);
    setEditingOptionErrors({});
    setRenameError(null);
  }, []);

  const handleAddEditingOption = () => {
    setEditingAddError(null);
    const raw = editingOptionInput.trim();
    if (!raw) {
      setEditingAddError('Please enter an option label before adding.');
      return;
    }
    if (raw.length > 25) {
      setEditingAddError('Option label cannot exceed 25 characters.');
      return;
    }

    const normVal = normalizeLabel(raw);
    const exists = editingOptions.some((opt) => normalizeLabel(opt) === normVal);

    if (exists) {
      setEditingAddError('This option already exists.');
      return;
    }

    setEditingOptions((prev) => [...prev, raw]);
    setEditingOptionInput('');
  };

  const handleEditingOptionRename = useCallback((index: number, newLabel: string) => {
    setEditingOptions((prev) => {
      const updated = [...prev];
      updated[index] = newLabel;

      const trimmed = newLabel.trim();
      const norm = normalizeLabel(newLabel);

      setEditingOptionErrors((errPrev) => {
        const copy = { ...errPrev };
        if (!trimmed) {
          copy[index] = 'Option label cannot be empty.';
        } else if (newLabel.length > 25) {
          copy[index] = 'Option label cannot exceed 25 characters.';
        } else {
          // Check collision against other indices
          const hasDup = updated.some((other, idx) => idx !== index && normalizeLabel(other) === norm);
          if (hasDup) {
            copy[index] = 'This option already exists.';
          } else {
            delete copy[index];
          }
        }
        return copy;
      });

      return updated;
    });
  }, []);

  const handleRemoveEditingOption = useCallback((indexToRemove: number) => {
    // Prevent removing existing options loaded at open time (index < initialEditingOptions.length)
    if (indexToRemove < initialEditingOptions.length) return;

    setEditingAddError(null);
    setEditingOptions((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    setEditingOptionErrors((prev) => {
      const copy = { ...prev };
      delete copy[indexToRemove];
      return copy;
    });
  }, [initialEditingOptions.length]);

  const handleSaveRename = async () => {
    if (!editingKey || !editingName.trim()) return;
    setRenameError(null);
    setEditingAddError(null);

    const trimmedName = editingName.trim();
    const collisionError = checkNameCollision(trimmedName, editingKey);
    if (collisionError) {
      setRenameError(collisionError);
      return;
    }

    const targetCol = customColumns.find((c) => c.key === editingKey);
    const isSelectType = targetCol?.type === 'select' || targetCol?.type === 'multi_select';

    if (isSelectType) {
      if (editingOptions.length === 0) {
        setEditingAddError('Please add at least one choice option.');
        return;
      }
      if (Object.keys(editingOptionErrors).length > 0) {
        setEditingAddError('Please resolve option errors before saving.');
        return;
      }
    }

    try {
      const nameChanged = trimmedName !== targetCol?.name;
      const initialOpts = targetCol?.options || [];
      const finalOptions: string[] = isSelectType ? editingOptions.map((o) => o.trim()) : [];
      const optionsChanged =
        isSelectType &&
        (finalOptions.length !== initialOpts.length ||
          finalOptions.some((opt, idx) => opt !== initialOpts[idx]?.trim()));

      const patchPayload: { name?: string; options?: string[] } = {};
      if (nameChanged) {
        patchPayload.name = trimmedName;
      }
      if (optionsChanged) {
        patchPayload.options = finalOptions;
      }

      if (Object.keys(patchPayload).length > 0) {
        if (patchPayload.name) {
          setColumnsState((prev) =>
            prev.map((col) => (col.key === editingKey ? { ...col, label: patchPayload.name! } : col))
          );
        }
        await onPatchCustomColumn(editingKey, patchPayload);
      }
      showNotification('Column updated successfully!', 'success');
      handleCloseChoiceDialog();
    } catch (err: any) {
      const errMsg = extractErrorMessage(err, 'Failed to update column');
      if (errMsg.toLowerCase().includes('option') || errMsg.toLowerCase().includes('choice')) {
        setEditingAddError(errMsg);
      } else {
        setRenameError(errMsg);
      }
      showNotification(errMsg, 'error');
    }
  };

  const handleInitiateDeactivate = useCallback((key: string) => {
    const col = columnsState.find((c) => c.key === key) || customColumns.find((c) => c.key === key);
    const label = (col as ColumnConfigItem)?.label || (col as CustomColumnDefinition)?.name || 'this column';
    setDeletingCol({ key, label });
  }, [columnsState, customColumns]);

  const handleConfirmDeactivate = async () => {
    if (!deletingCol) return;
    try {
      await onDeactivateCustomColumn(deletingCol.key);
      showNotification(`Column "${deletingCol.label}" deleted successfully.`, 'success');
      setDeletingCol(null);
    } catch (err: any) {
      const errMsg = extractErrorMessage(err, 'Failed to delete column');
      showNotification(errMsg, 'error');
      setDeletingCol(null);
    }
  };

  const editingColumnType = editingKey
    ? customColumns.find((c) => c.key === editingKey)?.type
    : undefined;

  const isNameChanged = editingName.trim() !== initialEditingName.trim();
  const isOptionsChanged =
    editingOptions.length !== initialEditingOptions.length ||
    editingOptions.some((opt, idx) => opt.trim() !== initialEditingOptions[idx]?.trim());

  // Track if any existing options were renamed
  const hasOptionRenames = initialEditingOptions.some((orig, idx) => {
    return editingOptions[idx] !== undefined && editingOptions[idx].trim() !== orig.trim();
  });

  const isChoiceModified = isNameChanged || isOptionsChanged;

  return {
    activeTab,
    setActiveTab,
    columnsState,
    isUserModified,
    highlightedKey,
    newColName,
    setNewColName,
    newColType,
    optionsList,
    currentOptionInput,
    setCurrentOptionInput,
    nameError,
    setNameError,
    optionsError,
    setOptionsError,
    createOptionErrors,
    handleCreateOptionRename,
    editingKey,
    setEditingKey,
    editingName,
    setEditingName,
    editingColumnType,
    renameError,
    setRenameError,
    editingOptions,
    initialEditingOptions,
    initialEditingOptionsCount: initialEditingOptions.length,
    editingOptionInput,
    setEditingOptionInput,
    editingAddError,
    setEditingAddError,
    editingOptionErrors,
    isChoiceDialogOpen,
    setIsChoiceDialogOpen,
    handleCloseChoiceDialog,
    isChoiceModified,
    hasOptionRenames,
    deletingCol,
    setDeletingCol,
    sensors,
    handleDragEnd,
    handleToggleVisibility,
    handleSaveConfig,
    handleAddOption,
    handleRemoveOption,
    handleAddEditingOption,
    handleEditingOptionRename,
    handleRemoveEditingOption,
    handleTypeChange,
    handleCreateColumn,
    handleStartRename,
    handleSaveRename,
    handleInitiateDeactivate,
    handleConfirmDeactivate,
  };
}
